/**
 * Step 3c — plugin-scoped overrides for upstream NFS plugin APIs we customize.
 *
 * Under NFS, registering a custom API factory under our `app` plugin id
 * collides with the upstream plugin that already owns that API id
 * (API_FACTORY_CONFLICT). Instead, override the existing extension under the
 * upstream plugin's own pluginId via `withOverrides({ extensions: [...] })`.
 */

import catalogGraphPluginAlphaBase from '@backstage/plugin-catalog-graph/alpha';
import catalogPluginAlphaBase from '@backstage/plugin-catalog/alpha';
import catalogImportPluginAlphaBase from '@backstage/plugin-catalog-import/alpha';
import apiDocsPluginAlphaBase from '@backstage/plugin-api-docs/alpha';
import techdocsPluginAlphaBase from '@backstage/plugin-techdocs/alpha';
import scaffolderPluginAlphaBase from '@backstage/plugin-scaffolder/alpha';
import searchPluginAlphaBase from '@backstage/plugin-search/alpha';
import userSettingsPluginAlphaBase from '@backstage/plugin-user-settings/alpha';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CustomGraphNode } from '@openchoreo/backstage-plugin-react';
import type { Entity } from '@backstage/catalog-model';
import { isTechDocsAvailable } from '@backstage/plugin-techdocs';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { createTranslationMessages } from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  SwappableComponentBlueprint,
  TranslationBlueprint,
} from '@backstage/plugin-app-react';
import { PageLayout, Progress } from '@backstage/frontend-plugin-api';
import { PageLoader } from '@openchoreo/backstage-design-system';
import { OpenChoreoPageLayout } from '../components/PageLayout/OpenChoreoPageLayout';
import { catalogImportTranslationRef } from '@backstage/plugin-catalog-import/alpha';
import {
  catalogGraphApiRef,
  DefaultCatalogGraphApi,
  ALL_RELATIONS,
  ALL_RELATION_PAIRS,
} from '@backstage/plugin-catalog-graph';
import {
  catalogApiRef,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import { DefaultEntityPresentationApi } from '@backstage/plugin-catalog';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  type DiscoveryApi,
  type FetchApi,
  type IdentityApi,
} from '@backstage/core-plugin-api';
import { CatalogClient } from '@backstage/catalog-client';
import { CachingCatalogApi } from '@openchoreo/backstage-plugin-react';
import {
  RELATION_DEPLOYS_TO,
  RELATION_DEPLOYED_BY,
  RELATION_USES_PIPELINE,
  RELATION_PIPELINE_USED_BY,
  RELATION_HOSTED_ON,
  RELATION_HOSTS,
  RELATION_OBSERVED_BY,
  RELATION_OBSERVES,
  RELATION_INSTANCE_OF,
  RELATION_HAS_INSTANCE,
  RELATION_USES_WORKFLOW,
  RELATION_WORKFLOW_USED_BY,
  RELATION_BUILDS_ON,
  RELATION_BUILDS,
} from '@openchoreo/backstage-plugin-common';
import { KIND_ICONS } from '../kindIcons';
import { LogRowActionBlueprint } from '@openchoreo/backstage-plugin-openchoreo-observability/alpha';
import { InvestigateLogButton } from '@openchoreo/backstage-plugin-openchoreo-portal-assistant';

/**
 * Override `catalog-graph`'s default `api:catalog-graph` to include the
 * custom OpenChoreo relations (deploysTo, hostedOn, instanceOf, …). Without
 * this, custom relations don't render in entity Relations cards or the
 * catalog graph.
 */
export const catalogGraphPluginAlpha =
  catalogGraphPluginAlphaBase.withOverrides({
    extensions: [
      // Override `page:catalog-graph` to pass renderNode={CustomGraphNode}.
      catalogGraphPluginAlphaBase.getExtension('page:catalog-graph').override({
        params: {
          loader: () =>
            import('@backstage/plugin-catalog-graph').then(m => (
              <m.CatalogGraphPage renderNode={CustomGraphNode} />
            )),
        },
      }),
      catalogGraphPluginAlphaBase.getExtension('api:catalog-graph').override({
        params: defineParams =>
          defineParams({
            api: catalogGraphApiRef,
            deps: {},
            factory: () =>
              new DefaultCatalogGraphApi({
                knownRelations: [
                  ...ALL_RELATIONS,
                  RELATION_DEPLOYS_TO,
                  RELATION_DEPLOYED_BY,
                  RELATION_USES_PIPELINE,
                  RELATION_PIPELINE_USED_BY,
                  RELATION_HOSTED_ON,
                  RELATION_HOSTS,
                  RELATION_OBSERVED_BY,
                  RELATION_OBSERVES,
                  RELATION_INSTANCE_OF,
                  RELATION_HAS_INSTANCE,
                  RELATION_USES_WORKFLOW,
                  RELATION_WORKFLOW_USED_BY,
                  RELATION_BUILDS_ON,
                  RELATION_BUILDS,
                ],
                knownRelationPairs: [
                  ...ALL_RELATION_PAIRS,
                  [RELATION_DEPLOYS_TO, RELATION_DEPLOYED_BY],
                  [RELATION_USES_PIPELINE, RELATION_PIPELINE_USED_BY],
                  [RELATION_HOSTED_ON, RELATION_HOSTS],
                  [RELATION_OBSERVED_BY, RELATION_OBSERVES],
                  [RELATION_INSTANCE_OF, RELATION_HAS_INSTANCE],
                  [RELATION_USES_WORKFLOW, RELATION_WORKFLOW_USED_BY],
                  [RELATION_BUILDS_ON, RELATION_BUILDS],
                ],
                defaultRelationTypes: { exclude: [] },
              }),
          }),
      }),
    ],
  });

/**
 * Factory for the caching `catalogApiRef` implementation (the `api:catalog`
 * override below). Builds the same `CatalogClient` upstream does, then wraps it
 * in `CachingCatalogApi` so catalog reads flow through the shared OpenChoreo
 * `queryClient`.
 *
 * `getUserRef` resolves the signed-in user's entityRef per call (used only to
 * namespace cache keys). It deliberately does NOT memoize the promise:
 * `identityApi` already caches the session and dedupes token refresh internally,
 * and memoizing here would permanently pin the cache namespace to the pending
 * sentinel if the very first call resolved to undefined or rejected (e.g. a
 * query before sign-in completes) — it would never recover for the app's life.
 *
 * Exported so the wrapper wiring is unit-testable without driving the extension
 * blueprint machinery.
 */
export function createCachingCatalogApi(deps: {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  identityApi: IdentityApi;
}): CachingCatalogApi {
  const { discoveryApi, fetchApi, identityApi } = deps;
  const base = new CatalogClient({ discoveryApi, fetchApi });
  const getUserRef = () =>
    identityApi
      .getBackstageIdentity()
      .then(({ userEntityRef }) => userEntityRef)
      .catch(() => undefined);
  return new CachingCatalogApi(base, getUserRef);
}

/**
 * Override `catalog`'s default `api:catalog/entity-presentation` to provide
 * kind icons for OpenChoreo-specific entity kinds (Environment, DataPlane,
 * DeploymentPipeline, etc.) in the catalog graph and entity views.
 *
 * Also overrides `page:catalog` so /catalog renders the host's
 * `CustomCatalogPage` (kind-grouped picker + card grid layout) instead of
 * upstream's `DefaultCatalogPage`. Before the createApp feature reorder
 * landed, the legacy `<Route path="/catalog">` mount won; under the
 * reorder, upstream's NFS extension wins by default — so we override its
 * loader explicitly.
 *
 * The `page:catalog/entity` chrome override that mounts `OpenChoreoEntityLayout`
 * lives in `@openchoreo/backstage-plugin/alpha` as `openChoreoEntityPageOverride`
 * and is wired into `createPortalApp`'s `features` array. Everything about the
 * OC entity-page chrome — compact header, styled tab bar, delete / annotation
 * context-menu extras, existence-check empty state — ships from the plugin so
 * external adopters can adopt the same look with one import.
 */
export const catalogPluginAlpha = catalogPluginAlphaBase.withOverrides({
  extensions: [
    catalogPluginAlphaBase.getExtension('page:catalog').override({
      params: {
        // `noHeader: true` suppresses the NFS `PageLayout`'s built-in title
        // bar ("Catalog" link). The host's `CustomCatalogPage` mounts its own
        // `<PageWithHeader title="Catalog">`; without this we render two
        // page headers, one above the other.
        noHeader: true,
        loader: () =>
          import('../components/catalog/CustomCatalogPage').then(m => (
            <m.CustomCatalogPage initialKind="system" />
          )),
      },
    }),
    // Wrap `api:catalog` with a caching CatalogApi so the catalog list
    // (`useEntityList` → getEntities/queryEntities) and the entity page
    // (getEntityByRef) read through the shared OpenChoreo `queryClient`.
    // Without this, Backstage's own catalog hooks re-fetch on every mount and
    // flash a skeleton on revisit; with it, a revisited catalog surface paints
    // instantly from the warm cache and revalidates in the background. Mirrors
    // upstream's factory (`new CatalogClient({ discoveryApi, fetchApi })`) and
    // adds `identityApi` to namespace cache keys per signed-in user.
    catalogPluginAlphaBase.getExtension('api:catalog').override({
      params: defineParams =>
        defineParams({
          api: catalogApiRef,
          deps: {
            discoveryApi: discoveryApiRef,
            fetchApi: fetchApiRef,
            identityApi: identityApiRef,
          },
          factory: createCachingCatalogApi,
        }),
    }),
    catalogPluginAlphaBase
      .getExtension('api:catalog/entity-presentation')
      .override({
        params: defineParams =>
          defineParams({
            api: entityPresentationApiRef,
            deps: { catalogApi: catalogApiRef },
            factory: ({ catalogApi }) =>
              DefaultEntityPresentationApi.create({
                catalogApi,
                kindIcons: KIND_ICONS,
              }),
          }),
      }),
  ],
});

/**
 * True when the component entity provides or consumes at least one API.
 * Ports the legacy `hasApis` predicate that the pre-NFS `EntityPage.tsx`
 * used to gate the `/apis` tab. Upstream `apiDocsPluginAlpha` only filters by
 * `kind:component` — every component would otherwise show an empty APIs tab.
 */
function hasApis(entity: Entity): boolean {
  if (entity.kind.toLowerCase() !== 'component') return false;
  return (
    entity.relations?.some(
      r => r.type === 'providesApi' || r.type === 'consumesApi',
    ) ?? false
  );
}

/**
 * Override upstream's `entity-content:api-docs/apis` (the "APIs" component
 * tab) to hide the tab unless the component actually provides or consumes
 * an API. Upstream's default `filter: { kind: 'component' }` shows the tab
 * on every component regardless of whether it has APIs; the portal has
 * always hidden it via `hasApis(entity)` in the legacy `EntityPage.tsx`.
 */
export const apiDocsPluginAlpha = apiDocsPluginAlphaBase.withOverrides({
  extensions: [
    apiDocsPluginAlphaBase
      .getExtension('entity-content:api-docs/apis')
      .override({
        params: { filter: hasApis },
      }),
    // /api-docs → host's CustomApiExplorerPage instead of upstream's default.
    apiDocsPluginAlphaBase.getExtension('page:api-docs').override({
      params: {
        // CustomApiExplorerPage mounts its own <PageWithHeader title="APIs">.
        noHeader: true,
        loader: () =>
          import('../components/catalog/CustomApiExplorerPage').then(m => (
            <m.CustomApiExplorerPage />
          )),
      },
    }),
    // Portal-only: raw OpenAPI YAML viewer instead of upstream's ApiDefinitionCard.
    apiDocsPluginAlphaBase
      .getExtension('entity-content:api-docs/definition')
      .override({
        params: {
          loader: async () =>
            import('../components/apiOverview/ApiOpenApiDefinitionTab').then(
              m => <m.ApiOpenApiDefinitionTab />,
            ),
        },
      }),
  ],
});

// /catalog-import → host's CustomCatalogImportPage, gated by permission.
export const catalogImportPluginAlpha =
  catalogImportPluginAlphaBase.withOverrides({
    extensions: [
      catalogImportPluginAlphaBase
        .getExtension('page:catalog-import')
        .override({
          params: {
            // CustomCatalogImportPage mounts its own <Page><Header>.
            noHeader: true,
            loader: () =>
              import('../components/catalog/CustomCatalogImportPage').then(
                m => (
                  <RequirePermission permission={catalogEntityCreatePermission}>
                    <m.CustomCatalogImportPage />
                  </RequirePermission>
                ),
              ),
          },
        }),
    ],
  });

// Hide TechDocs entity tab unless `backstage.io/techdocs-ref` is set.
export const techdocsPluginAlpha = techdocsPluginAlphaBase.withOverrides({
  extensions: [
    techdocsPluginAlphaBase.getExtension('entity-content:techdocs').override({
      params: { filter: isTechDocsAvailable },
    }),
  ],
});

/**
 * Override `scaffolder`'s default `page:scaffolder` (disabled — the legacy
 * `<ScaffolderPage>` mount at `/create` wins) and
 * `api:scaffolder/form-decorators` to inject the user's OpenChoreo token as
 * a secret for user-based authorization in scaffolder actions.
 */
/**
 * App-scoped extensions:
 * - SignInPage: lazy-loaded DynamicSignInPage that switches between
 *   OpenChoreo OIDC and guest mode based on `openchoreo.features.auth.enabled`.
 *   Replaces the legacy `createApp.components.SignInPage` slot.
 * - Translation overrides for catalog-import that previously rode via
 *   `createApp.__experimentalTranslations`. Customizes the header strings to
 *   read "Register an existing catalog entity" rather than the upstream
 *   default "Register Software".
 */
export const customAppModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: () =>
          import('../components/DynamicSignInPage').then(m => m.default),
      },
    }),
    TranslationBlueprint.make({
      name: 'catalog-import-overrides',
      params: {
        resource: createTranslationMessages({
          ref: catalogImportTranslationRef,
          full: false,
          messages: {
            'defaultImportPage.headerTitle':
              'Register an existing catalog entity',
            'defaultImportPage.contentHeaderTitle':
              'Start tracking your entity in {{appTitle}}',
            'defaultImportPage.supportTitle':
              'Start tracking your entity in {{appTitle}} by adding it to the software catalog.',
            'importInfoCard.title': 'Register an existing catalog entity',
            'stepInitAnalyzeUrl.urlHelperText':
              'Enter the full path to your entity file to start tracking',
            'stepFinishImportLocation.locations.viewButtonText': 'View Entity',
          },
        }),
      },
    }),
    // Host-injected per-row action renderer for the observability
    // runtime-logs tables. Wires the portal-assistant's
    // InvestigateLogButton into ObservabilityRuntimeLogs /
    // ObservabilityProjectRuntimeLogs without coupling the
    // observability plugin to portal-assistant. Mirrors upstream's
    // FormDecoratorBlueprint registration pattern.
    LogRowActionBlueprint.make({
      name: 'investigate-log',
      params: {
        renderer: (log, getLogsSnapshot) => (
          <InvestigateLogButton log={log} getLogsSnapshot={getLogsSnapshot} />
        ),
      },
    }),
    // Swap Backstage's built-in `<Progress />` bar (the Suspense fallback
    // `ExtensionBoundary` renders while a lazy page/extension chunk loads) for
    // our centered PageLoader, so route transitions match the rest of the app.
    SwappableComponentBlueprint.make({
      name: 'progress',
      params: defineParams =>
        defineParams({
          component: Progress,
          loader: () => () => <PageLoader />,
        }),
    }),
    // Swap the page chrome Backstage wraps routable pages in. Upstream renders a
    // `@backstage/ui` toolbar; every OpenChoreo page sets `noHeader: true` and
    // mounts its own MUI `<Page><Header>`, so the two only ever met on the Platform
    // section, whose tabs come from Backstage's sub-page mechanism and therefore
    // get their header from here. `OpenChoreoPageLayout` gives a tabbed page the
    // same purple header the rest of the portal uses and leaves every other page
    // exactly as upstream had it.
    SwappableComponentBlueprint.make({
      name: 'page-layout',
      params: defineParams =>
        defineParams({
          component: PageLayout,
          loader: () => OpenChoreoPageLayout,
        }),
    }),
  ],
});

// /search → host's SearchPage (software-catalog only, kind dropdown filter).
export const searchPluginAlpha = searchPluginAlphaBase.withOverrides({
  extensions: [
    searchPluginAlphaBase.getExtension('page:search').override({
      params: {
        noHeader: true,
        loader: () =>
          import('../components/search/SearchPage').then(m => (
            <SearchContextProvider>{m.searchPage}</SearchContextProvider>
          )),
      },
    }),
  ],
});

// /settings → legacy SettingsLayout chrome with three curated tabs
// (General / Access Control / Secrets). Bypasses upstream SubPageBlueprint
// collection.
export const userSettingsPluginAlpha =
  userSettingsPluginAlphaBase.withOverrides({
    extensions: [
      userSettingsPluginAlphaBase.getExtension('page:user-settings').override({
        params: {
          noHeader: true,
          loader: () =>
            import('../components/settings/OpenChoreoUserSettingsPage').then(
              m => <m.OpenChoreoUserSettingsPage />,
            ),
        },
      }),
    ],
  });

export const scaffolderPluginAlpha = scaffolderPluginAlphaBase.withOverrides({
  extensions: [
    // Override `page:scaffolder`'s loader to render the host's
    // `<ScaffolderPage>` composition (CustomTemplateListPage,
    // CustomReviewStep, and 27 field extensions). Same reason as the
    // `page:catalog` override above: the createApp feature reorder lets
    // upstream's NFS scaffolder page win by default, which would drop
    // every host customization.
    scaffolderPluginAlphaBase.getExtension('page:scaffolder').override({
      params: {
        // Same `noHeader: true` reason as the `page:catalog` override above
        // — `OpenChoreoScaffolderPage` mounts a `<ScaffolderPage
        // headerOptions={{ title: 'Create a new resource', ... }}>` that
        // renders its own page header.
        noHeader: true,
        loader: () =>
          import('../components/scaffolder/OpenChoreoScaffolderPage').then(
            m => <m.OpenChoreoScaffolderPage />,
          ),
      },
    }),
    // (The OpenChoreo IDP-token form decorator is now shipped by
    // `@openchoreo/backstage-plugin`'s alpha via `FormDecoratorBlueprint`.
    // Portal-app gets it through the normal plugin channel — no override
    // needed here. See `plugins/openchoreo/src/scaffolder/openChoreoTokenDecorator.ts`.)
  ],
});
